/******/ (() => { // webpackBootstrap
class CartFreeShippingBar extends HTMLElement {
  constructor() {
    super();
    
    // Milestone thresholds (in pounds) - will be set from data attributes
    this.freeDeliveryThreshold = 40;
    this.freeGiftThreshold = 80;
    this.freeGiftEnabled = true;
    
    // Elements
    this.messageElement = null;
    this.progressFill = null;
    this.deliveryMilestone = null;
    this.giftMilestone = null;
    
    // Bind methods
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.updateDisplay = this.updateDisplay.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-free-shipping-bar'});
    
    // Get thresholds from data attributes if available
    if (this.dataset.deliveryThreshold) {
      this.freeDeliveryThreshold = parseFloat(this.dataset.deliveryThreshold);
    }
    if (this.dataset.giftThreshold) {
      this.freeGiftThreshold = parseFloat(this.dataset.giftThreshold);
    }
    if (this.dataset.giftEnabled !== undefined) {
      this.freeGiftEnabled = this.dataset.giftEnabled === 'true';
    }
    
    // Get DOM elements
    this.messageElement = this.querySelector('.wt-free-shipping-bar p');
    this.progressFill = this.querySelector('.wt-progress-bar__fill');
    this.deliveryMilestone = this.querySelector('.wt-milestone--delivery');
    this.giftMilestone = this.querySelector('.wt-milestone--gift');
    
    // Hide gift milestone if free gift is disabled
    if (!this.freeGiftEnabled && this.giftMilestone) {
      this.giftMilestone.style.display = 'none';
    }
    
    // Listen for cart update events
    window.eventBus.on('update:cart:drawer', this.handleCartUpdate);
    window.eventBus.on('cart:added', this.fetchAndUpdate);
    
    // Initial update
    this.fetchAndUpdate();
  }

  disconnectedCallback() {
    // Clean up event listeners
    window.eventBus.off('update:cart:drawer', this.handleCartUpdate);
    window.eventBus.off('cart:added', this.fetchAndUpdate);
  }

  handleCartUpdate(eventBus, response) {
    // When cart is updated via the update:cart:drawer event, we get the response
    // The eventBus is passed as first parameter by the event system
    const cartData = response || eventBus;
    
    if (cartData && typeof cartData.total_price !== 'undefined') {
      this.updateFromCart(cartData);
    } else {
      // Fallback to fetching cart data
      this.fetchAndUpdate();
    }
  }

  fetchAndUpdate = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateFromCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  updateFromCart(cart) {
    // Validate cart data
    if (!cart || typeof cart.total_price === 'undefined' || cart.total_price === null) {
      console.warn('Invalid cart data received:', cart);
      return;
    }
    
    // Get cart value in pounds
    const cartValue = cart.total_price / 100;
    
    // Extra validation to ensure we have a valid number
    if (isNaN(cartValue) || !isFinite(cartValue)) {
      console.warn('Invalid cart value calculated:', cartValue);
      return;
    }
    
    // Calculate remaining amounts
    const deliveryRemaining = Math.max(0, this.freeDeliveryThreshold - cartValue);
    const giftRemaining = Math.max(0, this.freeGiftThreshold - cartValue);
    
    // Determine milestone status
    const deliveryUnlocked = cartValue >= this.freeDeliveryThreshold;
    const giftUnlocked = this.freeGiftEnabled && cartValue >= this.freeGiftThreshold;
    
    // Calculate progress percentage based on whether free gift is enabled
    let progressPercentage;
    if (this.freeGiftEnabled) {
      progressPercentage = Math.min(100, (cartValue / this.freeGiftThreshold) * 100);
    } else {
      progressPercentage = Math.min(100, (cartValue / this.freeDeliveryThreshold) * 100);
    }
    
    // Determine message
    let message = '';
    if (this.freeGiftEnabled) {
      if (giftUnlocked) {
        message = 'Free Shipping & Free Gift Unlocked!';
      } else if (deliveryUnlocked) {
        const remainingAmount = this.formatMoney(giftRemaining * 100);
        message = `Spend ${remainingAmount} more, get a free gift!`;
      } else {
        const remainingAmount = this.formatMoney(deliveryRemaining * 100);
        message = `Spend ${remainingAmount} more for free delivery!`;
      }
    } else {
      if (deliveryUnlocked) {
        message = 'Free Shipping Unlocked!';
      } else {
        const remainingAmount = this.formatMoney(deliveryRemaining * 100);
        message = `Spend ${remainingAmount} more for free delivery!`;
      }
    }
    
    // Update the display with animation
    this.updateDisplay(progressPercentage, message, deliveryUnlocked, giftUnlocked);
  }

  updateDisplay(progressPercentage, message, deliveryUnlocked, giftUnlocked) {
    // Update message
    if (this.messageElement) {
      this.messageElement.textContent = message;
    }
    
    // Animate progress bar fill
    if (this.progressFill) {
      // Use requestAnimationFrame for smooth animation
      requestAnimationFrame(() => {
        this.progressFill.style.width = `${progressPercentage}%`;
      });
    }
    
    // Update milestone states with animation
    if (this.deliveryMilestone) {
      if (deliveryUnlocked) {
        this.deliveryMilestone.classList.add('wt-milestone--unlocked');
      } else {
        this.deliveryMilestone.classList.remove('wt-milestone--unlocked');
      }
    }
    
    if (this.giftMilestone) {
      if (giftUnlocked) {
        this.giftMilestone.classList.add('wt-milestone--unlocked');
      } else {
        this.giftMilestone.classList.remove('wt-milestone--unlocked');
      }
    }
  }

  formatMoney(pence) {
    // Use Shopify's money format if available, otherwise use a simple fallback
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(pence, window.theme?.moneyFormat || '£{{amount}}');
    }
    
    // Simple fallback
    const pounds = (pence / 100).toFixed(2);
    return `£${pounds}`;
  }
}

customElements.define('cart-free-shipping-bar', CartFreeShippingBar);
/******/ })();

